import { z } from "zod";
/**
 * §6.3.5 interpretation labels. Mirrors the constants exported by the
 * backend's `services/health-score.ts`. Kept narrow (literal union) so
 * the shared schema rejects any drift if the backend's interpretation
 * table ever broadens.
 */
export const communityHealthLabelSchema = z.enum([
    "Excellent",
    "Good",
    "Needs Attention",
    "At Risk",
]);
export const communityHealthColorSchema = z.enum([
    "green",
    "blue",
    "yellow",
    "red",
]);
/**
 * One day in the 30-day trend. `healthScore` is a per-day snapshot computed
 * from that day's MetricHourly rollup (so the trend reads only from the
 * cached aggregate, never recomputing 30 separate windows). Days with no
 * MetricHourly data are emitted as `healthScore=0` to keep the chart x-axis
 * continuous (see TASK-028 AC: "backfilled with zeros where data is
 * missing").
 *
 * `date` is a UTC YYYY-MM-DD calendar day — the same format used by the
 * 7-day trend on the Overview API (TASK-027).
 */
export const communityHealthDailyPointSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    healthScore: z.number().int().min(0).max(100),
});
/**
 * Full breakdown payload — PRO/AGENCY only. The components are floats so the
 * UI can render fractional bars; the headline `healthScore` is the rounded
 * 0-100 integer per §6.3.5.
 *
 * `engagementComponent`, `growthComponent`, `activityComponent` correspond
 * 1:1 with the §6.3.5 weighted inputs (40% / 30% / 30%). `netGrowthRate`
 * and `dauMauRatio` are the underlying signal values, exposed so the UI
 * can render tooltip text like "+2.3% / week" without re-deriving from
 * the raw counts.
 */
export const communityHealthBreakdownSchema = z.object({
    engagementComponent: z.number().min(0).max(100),
    growthComponent: z.number().min(0).max(100),
    activityComponent: z.number().min(0).max(100),
    netGrowthRate: z.number(),
    dauMauRatio: z.number().min(0),
});
/**
 * Full response for PRO/AGENCY users. `upgradeRequired` is the literal
 * `false`, which (combined with the truncated branch's `true`) gives the
 * frontend a discriminated union it can switch on without checking for the
 * presence of `breakdown`.
 *
 * `dataAvailable` is FALSE iff the community has no MetricHourly rows at
 * all — a cold-start case that mirrors TASK-027's overview behavior. The
 * Mini App branches on this flag to render the onboarding empty state for
 * the health card too.
 */
export const communityHealthFullResponseSchema = z.object({
    upgradeRequired: z.literal(false),
    healthScore: z.number().int().min(0).max(100),
    label: communityHealthLabelSchema,
    color: communityHealthColorSchema,
    breakdown: communityHealthBreakdownSchema,
    trend: z.array(communityHealthDailyPointSchema).length(30),
    dataAvailable: z.boolean(),
    generatedAt: z.string().datetime(),
});
/**
 * Truncated response for FREE users. Includes the headline score + label +
 * color (so the Mini App can still render the gauge as a tease) but
 * omits the breakdown and trend. The upgrade CTA fields advertise which
 * plan unlocks the full data; the frontend renders a "Upgrade to Pro"
 * button next to the gauge.
 */
export const communityHealthTruncatedResponseSchema = z.object({
    upgradeRequired: z.literal(true),
    healthScore: z.number().int().min(0).max(100),
    label: communityHealthLabelSchema,
    color: communityHealthColorSchema,
    currentPlan: z.literal("FREE"),
    requiredPlan: z.literal("PRO"),
    upgradeCta: z.string().min(1),
    dataAvailable: z.boolean(),
    generatedAt: z.string().datetime(),
});
/**
 * Discriminated union over `upgradeRequired`. The frontend narrows via
 * `if (response.upgradeRequired)` and gets full type safety on the
 * remaining fields.
 */
export const communityHealthResponseSchema = z.discriminatedUnion("upgradeRequired", [
    communityHealthFullResponseSchema,
    communityHealthTruncatedResponseSchema,
]);
/**
 * Number of trailing daily trend points. Pinned as a constant so the
 * backend projector and the Zod `.length(30)` constraint share one
 * source of truth.
 */
export const COMMUNITY_HEALTH_TREND_DAYS = 30;
//# sourceMappingURL=health.js.map